import QuizInteractiveFlow from "@/components/storyboard/quiz/QuizInteractiveFlow";
import QuizCodeDebug from "@/components/storyboard/quiz/QuizCodeDebug";
import { deriveDefaultLanguage } from "@/components/storyboard/code/codeUtils";
import { useCappedCodeQuiz } from "@/hooks/useCappedCodeQuiz";
import type { ValidationResult } from "@/services/QuizValidationService";
import type { QuizCodeDebugEntryData } from "@/types/storyboard";

export interface QuizCodeDebugFlowProps {
  id: string;
  title: string;
  entries: QuizCodeDebugEntryData[];
  onValidate: (code: string, entryIndex: number) => Promise<ValidationResult>;
}

/**
 * Code debug quiz embedded in the shared interactive-element flow. The editor is
 * seeded from the learner's saved submissions (falling back to the buggy code);
 * "Code prüfen" runs server-side (LLM) validation, which also persists the code.
 * No localStorage — durable state lives in validation_data, and so do the
 * verdicts the card recaps.
 *
 * Each task grants a limited number of tries, tracked per task: the learner can
 * move between tasks freely, and the quiz ends once none of them can be worked
 * on any more.
 */
const QuizCodeDebugFlow = ({
  id,
  title,
  entries,
  onValidate,
}: QuizCodeDebugFlowProps) => {
  const language = deriveDefaultLanguage(
    entries.flatMap((entry) => [
      entry.codeWithBugs,
      entry.solution ?? entry.codeSolution,
    ])
  );

  const {
    submissions,
    refresh,
    statusOverride,
    completedCtaLabel,
    showCloseCta,
  } = useCappedCodeQuiz({
    id,
    prompts: entries.map(
      (entry) => entry.challenge ?? entry.debugChallenge ?? ""
    ),
    language,
  });

  return (
    <QuizInteractiveFlow
      id={id}
      title={title}
      illustrationKind="code_debug"
      size="wide"
      supportsInProgress
      statusOverride={statusOverride}
      completedCtaLabel={completedCtaLabel}
      showCloseCta={showCloseCta}
      showCompletedBadge={false}
      renderQuiz={() => (
        <QuizCodeDebug
          entries={entries}
          onValidate={onValidate}
          initialSubmissions={submissions}
          onSubmitted={refresh}
        />
      )}
    />
  );
};

export default QuizCodeDebugFlow;
